import * as React from "react";
import { useLocation } from "@remix-run/react";
import { Loader2 } from "lucide-react";

import { cn } from "~app/lib/utils";
import { Button } from "~app/components/ui/button";
import { Textarea } from "~app/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~app/components/ui/dialog";
import { EMAIL_CONSTANTS } from "~app/constants/email";

// TODO: optional screenshot via html2canvas (v1 is text-only — the Sentry
// screenshot depended on its SDK; reliable text capture is the priority).

// Aubergine feedback theme (matches the old themed Sentry dialog).
const FEEDBACK_BG = "#3F3239";
const FEEDBACK_FG = "#E2EFD5";

type FeedbackContextValue = {
  /** Open the first-party feedback dialog. */
  openFeedback: () => void;
};

const FeedbackContext = React.createContext<FeedbackContextValue | null>(null);

/** Access the feedback opener. Returns a no-op opener outside the provider so
 *  triggers never crash if mounted without it. */
export function useFeedback(): FeedbackContextValue {
  const ctx = React.useContext(FeedbackContext);
  return ctx ?? { openFeedback: () => {} };
}

/** Derive the org slug from the current path (/org/:orgSlug/...). Feedback
 *  posts to that org's same-origin endpoint. */
function useOrgSlug(): string | null {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const orgIndex = segments.indexOf("org");
  if (orgIndex !== -1 && segments[orgIndex + 1]) {
    return segments[orgIndex + 1] ?? null;
  }
  return null;
}

type SubmitState = "idle" | "submitting" | "success" | "error";

/** Provider + first-party feedback dialog. Mount once near the app root; any
 *  descendant opens it via useFeedback().openFeedback(). Submits same-origin
 *  (ad-blocker-proof) to /org/:orgSlug/feedback. */
export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);

  const openFeedback = React.useCallback(() => setOpen(true), []);

  const value = React.useMemo<FeedbackContextValue>(
    () => ({ openFeedback }),
    [openFeedback]
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </FeedbackContext.Provider>
  );
}

function FeedbackDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const orgSlug = useOrgSlug();
  const location = useLocation();
  const [message, setMessage] = React.useState("");
  const [state, setState] = React.useState<SubmitState>("idle");
  const [errorText, setErrorText] = React.useState<string | null>(null);

  // Reset transient state whenever the dialog opens.
  React.useEffect(() => {
    if (open) {
      setState("idle");
      setErrorText(null);
    }
  }, [open]);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = message.trim();
      if (!trimmed) {
        setErrorText("Please enter your feedback before sending.");
        return;
      }
      if (!orgSlug) {
        setErrorText(
          `We couldn't attach this to your organization. Please email ${EMAIL_CONSTANTS.SUPPORT_EMAIL}.`
        );
        return;
      }

      setState("submitting");
      setErrorText(null);

      try {
        const response = await fetch(`/org/${orgSlug}/feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: trimmed,
            page: location.pathname,
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        setState("success");
        setMessage("");
      } catch {
        setState("error");
        setErrorText(
          "Something went wrong sending your feedback. Please try again."
        );
      }
    },
    [message, orgSlug, location.pathname]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-0 sm:max-w-md"
        style={{ background: FEEDBACK_BG, color: FEEDBACK_FG }}
      >
        {state === "success" ? (
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle style={{ color: FEEDBACK_FG }}>
                Thanks — got it
              </DialogTitle>
              <DialogDescription style={{ color: "rgba(226,239,213,0.72)" }}>
                Your feedback went straight to the Yield team. We read every
                note.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                style={{
                  background: "rgba(48,101,114,0.72)",
                  color: FEEDBACK_FG,
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle style={{ color: FEEDBACK_FG }}>
                Share product feedback
              </DialogTitle>
              <DialogDescription style={{ color: "rgba(226,239,213,0.72)" }}>
                Tell us what&apos;s working, what feels off, or what you need
                next. Sent as the signed-in user.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="feedback-message"
                className="text-sm font-medium"
                style={{ color: FEEDBACK_FG }}
              >
                What happened?
              </label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                required
                maxLength={5000}
                rows={5}
                autoFocus
                placeholder="Tell us what's working, what feels off, or what you need next."
                className={cn(
                  "min-h-28 border-0 focus-visible:ring-2 focus-visible:ring-[rgba(157,193,129,0.45)]"
                )}
                style={{
                  background: "rgba(255,255,255,0.06)",
                  color: FEEDBACK_FG,
                }}
              />
            </div>

            {errorText && (
              <p className="text-sm" style={{ color: "#F0A9B2" }} role="alert">
                {errorText}
              </p>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                style={{ color: "rgba(226,239,213,0.72)" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={state === "submitting"}
                style={{
                  background: "rgba(48,101,114,0.72)",
                  color: FEEDBACK_FG,
                }}
              >
                {state === "submitting" && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Send feedback
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
